import React, { useEffect, useState } from 'react';
import './RegulatoryBodyReport.scss';
import PropTypes from 'prop-types';

import Paper from '@mui/material/Paper';
import { DataGrid, gridSortedRowIdsSelector, GridToolbarContainer, useGridApiContext } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';

import DeviceLink from 'src/component/UI/DeviceLink';
import ExportMenu from 'src/component/UI/ExportMenu';
import CsvIcon from 'src/assets/icons/csv.svg';
import PdfIcon from 'src/assets/icons/pdf.svg';
import { getReportPDFLoading, getReportData, getReportLoading } from 'src/module/device/selector';
import { Box, Typography } from '@mui/material';
import { formatDate } from 'src/utils/utils';
import { usePDFServices } from 'src/hooks/useReportData';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import moment from 'moment';

import { generateTestingReportPdf } from 'src/module/device/action';
import { useTranslation } from 'src/i18n';
import { isWhitelabel } from 'src/utils/utils';

function CustomToolbar ({ filters }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiRef = useGridApiContext();
  const pdfLoading = useSelector(getReportPDFLoading);
  const { usePDFServicesValue, usePDFServicesLoading } = usePDFServices();
  const whitelabel = isWhitelabel();

  const getUnfilteredRows = ({ apiRef: ref }) => gridSortedRowIdsSelector(ref);

  const handleExport = (options) => {
    if (apiRef && apiRef.current) {
      const csvString = apiRef.current.getDataAsCsv({
        ...options,
        escapeFormulas: true,
        delimiter: ',',
        shouldAppendQuotes: true
      });

      const BOM = '\uFEFF';
      const csvWithBOM = BOM + csvString;

      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `regulatory-report-${new Date().toISOString().split('T')[0]}.csv`);
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const handlePdfGenerationSubmit = () => {
    if (!pdfLoading) {
      dispatch(generateTestingReportPdf('regulatory', filters.selectedFilters, null, filters.startDate ? moment(filters.startDate).format('YYYY-MM-DD') : null, filters.endDate ? moment(filters.endDate).format('YYYY-MM-DD') : null, whitelabel ? 'yes' : 'no'));
    }
  };

  return (
    <GridToolbarContainer>
      <Box sx={{ flexGrow: 1 }} />
      <ExportMenu
        options={[
          {
            key: 'csv',
            label: t('downloadCsv'),
            icon: <img src={CsvIcon} alt='' className='export-menu-file-icon' />,
            onClick: () => handleExport({ getRowsToExport: getUnfilteredRows }),
          },
          {
            key: 'pdf',
            label: t('downloadPdf'),
            icon: <img src={PdfIcon} alt='' className='export-menu-file-icon' />,
            loading: pdfLoading,
            disabled: pdfLoading,
            hidden: usePDFServicesLoading || !usePDFServicesValue,
            onClick: handlePdfGenerationSubmit,
          },
        ]}
      />
    </GridToolbarContainer>
  );
}


export default function RegulatoryBodyReport ({ filters }) {
  const { t } = useTranslation();
  const data = useSelector(getReportData('regulator'));
  const loading = useSelector(getReportLoading);
  const [hasRun, setHasRun] = useState(false);

  // Track if report has been run (not loading and data exists)
  useEffect(() => {
    if (!loading && data !== null && data !== undefined) {
      setHasRun(true);
    }
  }, [loading, data]);

  const columns = [
    {
      field: 'unit',
      headerName: t('common.unit'),
      minWidth: 50,
      flex: 1
    },
    {
      field: 'parent_tag',
      headerName: t('regulatoryReport.controller'),
      minWidth: 100,
      renderCell: (params) =>  (<DeviceLink label={params.value} controllerTag={params.value} />),
      flex: 1
    },
    {
      field: 'tag',
      headerName: t('regulatoryReport.circuit'),
      minWidth: 100,
      renderCell: (params) =>  (<DeviceLink label={params.value} circuitTag={params.value} controllerTag={params.row.parent_tag}/>),
      flex: 1
    },
    {
      field: 'line_description',
      headerName: t('regulatoryReport.lineDescription'),
      minWidth: 125,
      flex: 1
    },
    {
      field: 'measured_current',
      headerName: t('regulatoryReport.measuredCurrent'),
      minWidth: 125,
      flex: 1
    },
    {
      field: 'measured_voltage',
      headerName: t('regulatoryReport.measuredVoltage'),
      minWidth: 125,
      flex: 1
    },
    {
      field: 'circuit_inspection_passed',
      headerName: t('regulatoryReport.circuitInspection'),
      minWidth: 125,
      renderCell: (params) => {
        if (params.value) {
          return t('regulatoryReport.passed');
        }
        if (params.row.circuit_function_created_at) {
          return t('common.failed');
        }
        return '';
      },
      flex: 1
    },
    {
      field: 'circuit_function_created_at',
      headerName: t('regulatoryReport.inspectionDate'),
      minWidth: 140,
      valueFormatter: (params) => params.value ? formatDate(params.value) : '',
      flex: 1
    },
    {
      field: 'insulation_passed',
      headerName: t('regulatoryReport.insulationInspection'),
      minWidth: 150,
      renderCell: (params) => {
        if (params.value) {
          return t('regulatoryReport.passed');
        }
        if (params.row.insulation_created_at) {
          return t('common.failed');
        }
        return '';
      },
      flex: 1
    },
    {
      field: 'insulation_created_at',
      headerName: t('regulatoryReport.insulationDate'),
      minWidth: 140,
      type: 'date',
      valueFormatter: (params) => params.value ? formatDate(params.value) : '',
      flex: 1
    },
  ];

  return (
    <>
      {loading && <div style={{ width: '300px', height: '500px', margin: 'auto', paddingTop: '200px' }}>
        <LogoSpinner size={200} />
      </div> }

      {!loading && (
        <Paper style={{ marginTop: '20px' }}>
          <h2>{t('regulatoryReport.title')}</h2>

          {(!data || data.size === 0) ? (
            hasRun ? (
              <Box sx={{ padding: '40px', textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {t('reports.noResults')}
                </Typography>
              </Box>
            ) : null
          ) : (
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={data?.toJS()}
                columns={columns}
                sx={{
                  border: 'none', // Removes the outer border
                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none', // Removes the separator lines between column headers
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                }}

                density='compact'
                components={{
                  Toolbar: CustomToolbar,
                }}
                componentsProps={{
                  toolbar: { filters },
                }}
              />
            </Box>
          )}
        </Paper>
      )}
    </>
  );
}

CustomToolbar.propTypes = {
  filters: PropTypes.shape({
    selectedFilters: PropTypes.any,
    startDate: PropTypes.any,
    endDate: PropTypes.any,
  })
};
RegulatoryBodyReport.propTypes = {
  filters: PropTypes.object
};
RegulatoryBodyReport.defaultProps = {

};
